import React from 'react'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Button, Input, Form, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'antd/es/form/Form'
import { useSelector } from 'react-redux'
import { useLogin } from '../queries/useLogin'
import { currentUserHomepageSelector } from '../../../redux/session/currentUser/selectors'
import { Credentials } from '../types/Login'

const Login: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = useForm<Credentials>()
  const location = useLocation()
  const homepage = useSelector(currentUserHomepageSelector)
  const { mutateAsync: login, isPending: isLoggingIn, error } = useLogin()

  const errorMessage = t(`description:${error?.response?.data?.message}`, '') || t('errorMessage:DEFAULT')

  const handleLogin = async (credentials: Credentials) => {
    try {
      await login(credentials)
      navigate(location?.state?.from || homepage, { state: { fromLogin: true } })
    } catch (e) {
      // Error is already handled by the useLogin hook
      console.debug('Login failed, not navigating')
    }
  }

  const requiredRule = [{ required: true, message: t('global:required-field') }]

  return (
    <Form form={form} onFinish={handleLogin} layout="vertical" size="large" requiredMark={false}>
      <Typography.Title level={2} style={{ margin: '0 0 24px' }}>
        {t('global:login')}
      </Typography.Title>
      <Form.Item name="email" label={t('global:email')} rules={requiredRule}>
        <Input
          prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          name="email"
          type="email"
          autoComplete="email"
        />
      </Form.Item>
      <Form.Item name="password" label={t('global:password')} rules={requiredRule}>
        <Input.Password
          prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          name="password"
          autoComplete="current-password"
        />
      </Form.Item>
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text type="danger">{errorMessage}</Typography.Text>
        </div>
      )}
      <Form.Item>
        <Button block loading={isLoggingIn} type="primary" htmlType="submit" className="login-form-button">
          {t('global:login')}
        </Button>
      </Form.Item>
      <Link to="/user/forgot-password">{t('usersPage:forgot-password')}</Link>
    </Form>
  )
}

export default Login
